// ─────────────────────────────────────────────
//  roomRelease — WHEN a room is finished for the day, and what the board is
//  allowed to do about it on its own.
//
//  Owner, 2026-09-09: *"When the black time line pass the end of the or time and
//  no more cases to follow. Wait for m15 min, clear the room and remove it from
//  the time line and send the crna to unassigned"*.
//
//  ⚠️ THE 15 MINUTES ALREADY EXISTED AND IS NOT RE-TYPED HERE. `STAFF_BUFFER_MIN`
//  is the board's single answer to "how long after a case ends does the room
//  still claim its people", and `rowClaimsAt` — the predicate FREE NOW, the
//  bench, the staffing counts and the doctor coverage all share — is already
//  written in terms of it. So the CRNA in a room that ended at 14:00 is ALREADY
//  on the free list at 14:15 without anything in this file running. What was
//  missing is the room itself: it stayed on the chart all evening, and the room
//  document went on naming the people who had gone home.
//
//  ⚠️ A SECOND CONSTANT WOULD BE THE BUG. If the owner ever moves the buffer,
//  a room that has vanished from the chart while the board still counts its
//  CRNA as busy — or the reverse — is a contradiction nobody can read off the
//  screen. One number, imported.
//
//  ⚠️ AND THE RULE FAILS TOWARDS KEEPING THE ROOM. Every uncertainty below —
//  a case with no end time, an on-call row, a room whose status says a case is
//  running — resolves to "leave it alone". A room that lingers an hour too long
//  is an untidy board; a room that vanishes while somebody is still in it is a
//  patient nobody is looking at.
//
//  Pure: no React, no clock of its own. The caller passes `nowMin`, for the same
//  reason every other timing module here does — a test that reads the wall clock
//  passes before lunch and fails after it.
// ─────────────────────────────────────────────
import { STAFF_BUFFER_MIN } from "./timeline";
import { normalizeStatus } from "./status";

// What the room may be doing, as far as this module is concerned.
export const OCCUPIED = "occupied"; // still claims its people — leave it alone
export const RELEASE = "release";   // finished, and safe to clear without asking
export const CONFIRM = "confirm";   // finished by the clock, but the room says a
                                    // case is running — ask a human

// ⚠️ THE TWO STATUSES THAT MEAN "SOMEBODY IS IN THERE RIGHT NOW". `ready` is
// NOT one of them: it means the room is set up for a case that has not started,
// which is the resting state of every room the importer creates and would
// therefore make every finished room ask a question — a prompt that fires on all
// 19 rooms every evening is a prompt nobody reads.
//
// `cancelled` and `empty` are not here either: both already mean the room is not
// running, so a finished case in one of them can go quietly.
const IN_PROGRESS = new Set(["active", "closing"]);

// ⚠️ AND A STATUS IS NOT A CLOCK — THE SCAR THIS REPO ALREADY CARRIES, WALKED
// INTO AGAIN. Owner, 2026-09-09, with rooms sitting on the chart well past the
// now-line: *"The time line already pass those rooms, those cases should
// disappear"*. They were not disappearing: their room docs still read `active`,
// because nobody walks the board at 20:00 marking finished cases done — the same
// reason `domain/departure` had to stop asking status whether a doctor was still
// covering rooms, where it produced "covering 5 rooms" at 20:49 for a doctor
// whose last case ended at 15:00.
//
// So the status only gets a vote when it is EVIDENCE ABOUT NOW. `room.updatedAt`
// is written by every room write, so a room last touched BEFORE its own case
// ended is saying nothing about the present: it is the importer's opening state,
// preserved. A room touched at or after the case's end is somebody actually
// maintaining it, and that is worth a question.
//
// ⚠️ THE UNKNOWN CASE RELEASES, and that is a deliberate reversal of the rest of
// this file's fail-towards-keeping rule. An absent `updatedAt` means the room
// doc has not been written since whatever seeded it, which is the strongest
// possible evidence that nobody is maintaining its status — treating it as a
// reason to hold would restore exactly the behaviour the owner reported.
function statusHoldsRoom(status, statusAtMin, lastEndMin) {
  if (!IN_PROGRESS.has(normalizeStatus(status))) return false;
  if (!Number.isFinite(statusAtMin) || !Number.isFinite(lastEndMin)) return false;
  return statusAtMin >= lastEndMin;
}

// Does this case still claim its room at `nowMin`?
//
// ⚠️ THE SAME SHAPE AS `rowClaimsAt`, ASKED OF A CASE. That predicate reads
// `row.startTime` / `row.endTime` (the stored strings); a `buildTimeline` case
// carries the resolved minutes, and re-parsing the strings here would be a
// second reading of the same fact. The three refusals are its refusals: no end
// time, an end at or before the start (a row the chart draws across the whole
// axis), and on-call — the on-call CRNA covers the night and their room is never
// finished by a clock.
function caseClaims(c, nowMin, bufferMin) {
  if (!c) return false;
  if (c.onCall) return true;
  const end = Number.isFinite(c.endMin) ? c.endMin : null;
  if (end == null) return true;
  const start = Number.isFinite(c.startMin) ? c.startMin : null;
  if (start != null && end <= start) return true;
  return end > nowMin - bufferMin;
}

/**
 * What should happen to one room.
 *
 * @param cases       that room's `buildTimeline` cases (the chart's own list)
 * @param status      the ROOM DOC's status, or null when unknown
 * @param statusAtMin when that room doc was last written, as minutes since
 *                    midnight on the same clock as `nowMin`, or null. Without
 *                    it the status cannot be dated and therefore cannot hold the
 *                    room — see `statusHoldsRoom`.
 * @param nowMin      minutes since midnight, facility-local
 * @param bufferMin   defaults to the board's one buffer
 * @returns { state, lastEndMin, finishedForMin }
 *          `lastEndMin` is when the room's last case ended and
 *          `finishedForMin` how long ago that was — both null while occupied,
 *          because a room that is still running has not finished at any time.
 */
export function roomReleaseState({ cases = [], status = null, statusAtMin = null, nowMin = null, bufferMin = STAFF_BUFFER_MIN } = {}) {
  const list = Array.isArray(cases) ? cases.filter(Boolean) : [];
  // ⚠️ NO CLOCK MEANS NO OPINION, the same reading `rowClaimsAt` takes of a null
  // `nowMin`. A board rendering before the first tick must not clear rooms.
  if (!Number.isFinite(nowMin)) return { state: OCCUPIED, lastEndMin: null, finishedForMin: null };
  // A room the chart is drawing always has at least one case; an empty list is
  // a room that was never occupied, and there is nothing to release.
  if (list.length === 0) return { state: OCCUPIED, lastEndMin: null, finishedForMin: null };
  // ⚠️ ANY case still claiming keeps the room — which is what "and no more cases
  // to follow" means. A room whose 07:00 case ended at 09:00 and whose next one
  // starts at 13:00 is not finished at 09:15; the later case claims it, because
  // its end is in the future.
  if (list.some((c) => caseClaims(c, nowMin, bufferMin))) {
    return { state: OCCUPIED, lastEndMin: null, finishedForMin: null };
  }
  const lastEndMin = list.reduce((hi, c) => (Number.isFinite(c.endMin) && c.endMin > hi ? c.endMin : hi), -Infinity);
  const finishedForMin = Number.isFinite(lastEndMin) ? Math.max(0, nowMin - lastEndMin) : null;
  // ⚠️ THE STATUS IS THE ONE THING THE CLOCK CANNOT SEE. A case that ran long
  // without anybody updating its end time is finished on paper and running in
  // fact, and clearing it would take a room in use off the board. Owner's
  // instruction for exactly this case, 2026-09-09: *"Give a warning in the right
  // side of the time line and give an option to close the room or keep it
  // open"*.
  if (statusHoldsRoom(status, statusAtMin, lastEndMin)) {
    return { state: CONFIRM, lastEndMin, finishedForMin };
  }
  return { state: RELEASE, lastEndMin, finishedForMin };
}

/**
 * Walk the chart's own sections and sort every room into the three states.
 *
 * ⚠️ IT READS THE SECTIONS, NOT THE ASSIGNMENTS. `buildTimeline` has already
 * decided which rooms are occupied, which cases belong to which room, and what
 * every case's minutes are; asking the raw rows again here would be a second
 * answer to those questions and the two would drift the first time either
 * changed.
 *
 * @param sections   `model.sections` from buildTimeline
 * @param roomStatus   (roomId) => status string | null
 * @param roomStatusAt (roomId) => when that room doc was last written, in
 *                     minutes since midnight, or null
 * @param heldIds    rooms a human pressed "Keep it open" on — never released,
 *                   and never asked about again
 * @returns { releaseIds, releaseRows, confirmRows }
 *          `releaseRows` and `confirmRows` are the same shape —
 *          { roomId, roomName, cases, lastEndMin, finishedForMin } — because the
 *          caller has to name the people in BOTH: the ones it clears on its own
 *          and the ones it asks about. `releaseIds` is that first list as a Set,
 *          which is what the chart filters its rooms against.
 */
export function roomReleaseRows({ sections = [], roomStatus = () => null, roomStatusAt = () => null, nowMin = null, heldIds = null, bufferMin = STAFF_BUFFER_MIN } = {}) {
  const releaseIds = new Set();
  const releaseRows = [];
  const confirmRows = [];
  const held = heldIds instanceof Set ? heldIds : new Set(heldIds || []);
  for (const sec of Array.isArray(sections) ? sections : []) {
    for (const room of sec?.rooms || []) {
      if (!room?.id || held.has(room.id)) continue;
      const { state, lastEndMin, finishedForMin } = roomReleaseState({
        cases: room.cases,
        status: roomStatus(room.id),
        statusAtMin: roomStatusAt(room.id),
        nowMin,
        bufferMin,
      });
      if (state === OCCUPIED) continue;
      const row = {
        roomId: room.id,
        roomName: room.name || "",
        cases: room.cases || [],
        lastEndMin,
        finishedForMin,
      };
      if (state === RELEASE) {
        releaseIds.add(room.id);
        releaseRows.push(row);
      } else {
        confirmRows.push(row);
      }
    }
  }
  // Longest-finished first: the room that has been done the longest is the one
  // most likely to be genuinely done, and the one an AIC should look at first.
  confirmRows.sort((a, b) => (b.finishedForMin ?? 0) - (a.finishedForMin ?? 0));
  return { releaseIds, releaseRows, confirmRows };
}

/**
 * Everyone a finished room lets go: the seat holder, the second provider
 * standing with them, and the supervising doctor.
 *
 * Owner, 2026-09-09, asked for all three — *"All the option"*. A trainee or a
 * doctor still attached to a room that has left the chart is a person the board
 * shows as busy and nobody can find.
 *
 * ⚠️ IDS ONLY, AND ONLY REAL ONES. `secondProviderMember` mints a synthetic
 * `sp:<name>` for somebody the importer never resolved; a roster write keyed by
 * one lands on a document that does not exist. Names are carried alongside so a
 * caller can still SAY who was released.
 */
export function releasedPeople(cases = []) {
  const out = [];
  const seen = new Set();
  const add = (id, name, role) => {
    const clean = String(name || "").trim();
    const real = id && !String(id).startsWith("sp:") ? id : null;
    const key = real || `name:${clean.toLowerCase()}`;
    if (!clean || seen.has(key)) return;
    seen.add(key);
    out.push({ id: real, name: clean, role });
  };
  for (const c of Array.isArray(cases) ? cases.filter(Boolean) : []) {
    add(c.ahpId, c.label || c.ahpName, "crna");
    const second = c.orientee || c.inlineOrientee || null;
    if (second) add(second.id, second.name, "second");
    add(c.attendingId, c.attendingName || c.attending, "md");
  }
  return out;
}
